import { useEffect, useState } from "react";
import {
  login,
  loginWithGoogle,
  resetPassword,
  registerAndSendVerification,
  checkVerificationAndDelete,
  logout,
  sendVerificationEmail,
  refreshUser,
} from "@auth/auth";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@context/authcontext";
import { InputField } from "@mmfestival/shared-components";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const resetPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

const registerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export const Login = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formMode, setFormMode] = useState("login");
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showInputs, setShowInputs] = useState(false);

  // verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [isResending, setIsResending] = useState(false);

  // LOGIN form
  const {
    handleSubmit: handleSubmitLogin,
    control: controlLogin,
    setValue: setValueLogin,
    formState: { errors: errorsLogin, isSubmitting: isSubmittingLogin },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
  });

  // RESET form
  const {
    handleSubmit: handleSubmitReset,
    control: controlReset,
    formState: { errors: errorsReset, isSubmitting: isSubmittingReset },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onTouched",
  });

  // REGISTER form
  const {
    handleSubmit: handleSubmitReg,
    control: controlReg,
    formState: { errors: errorsReg, isSubmitting: isSubmittingReg },
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: "onTouched",
  });

  // keep the page from scrolling while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Fill email from localStorage if present (after verification)
  useEffect(() => {
    if (formMode === "login") {
      const verifiedEmail = localStorage.getItem("verifiedEmail");
      if (verifiedEmail) {
        setValueLogin("email", verifiedEmail);
        localStorage.removeItem("verifiedEmail");
        setShowInputs(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode]);

  // Only redirect when user exists and email is verified
  useEffect(() => {
    if (user && user.emailVerified) {
      navigate("/Profile");
    }
  }, [user, navigate]);

  // Reset messages on mode change
  useEffect(() => {
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [formMode]);

  const handleModeChange = (mode) => {
    setFormMode(mode);
    setErrorMessage(null);
    setSuccessMessage(null);
    if (mode === "login") {
      setShowInputs(false);
    }
  };

  // Error normalizer (friendly English messages)
  const getFriendlyError = (error) => {
    if (!error) return "An error occurred. Please try again.";
    if (typeof error === "string" && !error.startsWith("Firebase: Error"))
      return error;

    const message = error && error.message ? String(error.message) : "";
    let code = error && error.code ? String(error.code) : "";

    if (!code && message) {
      const m = message.match(/\(auth\/([^)]+)\)/i);
      if (m && m[1]) code = m[1];
    }

    const c = (code || "").toLowerCase();
    const mLower = message.toLowerCase();

    if (
      c.includes("email-already-in-use") ||
      mLower.includes("email-already-in-use")
    ) {
      return "Email already in use. Please log in or use a different email.";
    }
    if (
      c.includes("invalid-email") ||
      mLower.includes("invalid-email") ||
      mLower.includes("invalid email")
    ) {
      return "Invalid email format.";
    }
    if (c.includes("weak-password") || mLower.includes("weak-password")) {
      return "Password is too weak. Please use a stronger password.";
    }
    if (c.includes("user-not-found") || mLower.includes("user-not-found")) {
      return "Email not found. Please register first.";
    }
    if (c.includes("wrong-password") || mLower.includes("wrong-password")) {
      return "Incorrect password. Please try again.";
    }
    if (
      c.includes("too-many-requests") ||
      mLower.includes("too-many-requests")
    ) {
      return "Too many attempts. Please try again later.";
    }
    if (
      c.includes("invalid-credential") ||
      mLower.includes("invalid-credential") ||
      mLower.includes("invalid credential")
    ) {
      return "Invalid credentials. Please check your login details.";
    }
    if (
      mLower.includes("belum diverifikasi") ||
      mLower.includes("verifikasi") ||
      mLower.includes("not verified") ||
      mLower.includes("verification")
    ) {
      return "Email not verified. Please check your inbox.";
    }
    if (message && !message.toLowerCase().startsWith("firebase: error")) {
      return message;
    }
    return "An error occurred. Please try again or contact support if the problem persists.";
  };

  // handle login
  const handleLogin = async (data) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);

      const signedUser = await login(data.email, data.password);

      // If signed-in but not verified -> show modal
      if (signedUser && !signedUser.emailVerified) {
        setVerificationEmail(signedUser.email || data.email || "");
        setShowVerificationModal(true);
        setErrorMessage("Please verify your email.");
        return;
      }

      // Otherwise navigate
      navigate("/Profile");
    } catch (error) {
      console.error("Login error:", error);
      const friendly = getFriendlyError(error);
      setErrorMessage(friendly);

      // show modal if error indicates unverified email (support both languages)
      const msgLower = (error?.message || "").toLowerCase();
      if (
        msgLower.includes("belum diverifikasi") ||
        msgLower.includes("verifikasi") ||
        msgLower.includes("not verified") ||
        msgLower.includes("verification")
      ) {
        setVerificationEmail(data?.email || "");
        setShowVerificationModal(true);
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    setErrorMessage(null);
    try {
      await loginWithGoogle();
      navigate("/Profile");
    } catch (error) {
      console.error("Google login error:", error);
      setErrorMessage(getFriendlyError(error));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleResetPassword = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await resetPassword(data.email);
      setSuccessMessage("Password reset link has been sent.");
    } catch (error) {
      console.error("Reset password error:", error);
      setErrorMessage(getFriendlyError(error));
    }
  };

  const handleRegister = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { message } = await registerAndSendVerification(
        data.email,
        data.name,
        data.password
      );

      setSuccessMessage(
        message || "Registration successful. Check your email to verify."
      );

      // schedule verification check & auto-back-to-login
      setTimeout(async () => {
        try {
          await checkVerificationAndDelete();
        } catch (e) {
          console.warn("checkVerificationAndDelete error:", e);
        }
      }, 120000);

      setTimeout(() => {
        setFormMode("login");
        setShowInputs(false);
      }, 5000);
    } catch (error) {
      console.error("Register error:", error);
      setErrorMessage(getFriendlyError(error));
    }
  };

  // resend verification handler for modal
  const handleResendVerification = async () => {
    setIsResending(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      // refresh current signed-in user (we keep them signed in on unverified login)
      const current = await refreshUser();
      if (!current) {
        setErrorMessage("Unable to send verification. Please log in again.");
        setIsResending(false);
        return;
      }

      await sendVerificationEmail(current);
      setSuccessMessage("Verification email sent.");
    } catch (err) {
      console.error("Resend verification error:", err);
      setErrorMessage(getFriendlyError(err));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-20">
      <motion.div
        className="relative z-0 max-w-4xl w-full mx-auto bg-white bg-opacity-40 border border-slate-200 backdrop-blur-md grid md:grid-cols-2 rounded-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        {/* LEFT: hero-like image with zoom + gradient overlay */}
        <div
          className="hidden md:flex flex-col justify-center text-white p-8 h-full relative rounded-tl-xl rounded-bl-xl overflow-hidden"
          aria-hidden // purely decorative / background
        >
          {/* Zooming background layer (pointer-events-none so it doesn't block) */}
          <motion.div
            className="absolute inset-0 pointer-events-none rounded-tl-xl rounded-bl-xl"
            style={{
              backgroundImage: "url('/bg/java.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundAttachment: "fixed",
            }}
            initial={{ scale: 1 }}
            animate={{ scale: 1.12 }}
            transition={{
              duration: 12,
              ease: "easeOut",
            }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5"></div>

          {/* Subtle blur / color wash (optional, keeps text legible) */}
          <div className="absolute inset-0 bg-cyan-600/10 backdrop-blur-sm z-15 rounded-tl-xl rounded-bl-xl"></div>

          {/* Content on top of overlays */}
          <motion.h1
            className="text-4xl font-bold mb-4 drop-shadow-md z-20 relative text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Welcome
          </motion.h1>
          <motion.p
            className="text-lg drop-shadow-md z-20 relative text-white max-w-[18rem]"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          >
            Sign in to access your account and enjoy the best experience with
            us.
          </motion.p>
        </div>

        {/* RIGHT: form area */}
        <div className="flex flex-col px-4 py-8 z-10">
          <AnimatePresence mode="wait">
            {formMode === "login" && (
              <motion.div
                key="login"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                <img
                  src="/logo/favicon.svg"
                  alt="Logo"
                  className="w-24 h-auto mx-auto mt-4 block"
                />
                <p className="text-xl font-semibold mb-4 text-center text-slate-700">
                  MOUNTAIN MARATHON FESTIVAL
                </p>

                {!showInputs && (
                  <>
                    <button
                      onClick={handleGoogleLogin}
                      className="w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 disabled:opacity-50 mt-4 flex items-center justify-center"
                      disabled={loadingGoogle}
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.51h5.84c-.25 1.37-1.02 2.53-2.16 3.31v2.75h3.49c2.04-1.88 3.23-4.65 3.23-7.77z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-1.01 7.28-2.73l-3.49-2.75c-1.01.68-2.3 1.08-3.79 1.08-2.91 0-5.37-1.97-6.25-4.61H2.36v2.9C4.17 20.99 7.83 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.75 14.24c-.22-.66-.34-1.37-.34-2.11s.12-1.45.34-2.11V7.12H2.36C1.49 8.71 1 10.34 1 12s.49 3.29 1.36 4.88l3.39-2.64z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.02c1.61 0 3.04.55 4.18 1.62l3.13-3.13C17.46 1.99 14.97 1 12 1 7.83 1 4.17 3.01 2.36 6.88l3.39 2.64c.88-2.64 3.34-4.5 6.25-4.5z"
                          fill="#EA4335"
                        />
                      </svg>
                      {loadingGoogle ? "Processing..." : "Sign in with Google"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowInputs(true)}
                      className="w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                          stroke="#4B5563"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 6L12 13L2 6"
                          stroke="#4B5563"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Sign in with Email
                    </button>
                    <p className="text-slate-600 text-sm text-center mt-12 px-4">
                      By joining, you agree to our{" "}
                      <Link
                        to="/SyaratKetentuan"
                        className="text-cyan-600 hover:underline"
                      >
                        Terms &amp; Conditions
                      </Link>
                      . Please read our{" "}
                      <Link
                        to="/KebijakanPrivasi"
                        className="text-cyan-600 hover:underline"
                      >
                        Privacy Policy
                      </Link>{" "}
                      to understand how we handle personal data.
                    </p>
                  </>
                )}

                {showInputs && (
                  <motion.form
                    onSubmit={handleSubmitLogin(handleLogin)}
                    className="flex flex-col gap-3"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <Controller
                      name="email"
                      control={controlLogin}
                      defaultValue=""
                      render={({ field }) => (
                        <InputField
                          label="Email"
                          type="email"
                          placeholder="Email"
                          {...field}
                          error={errorsLogin.email?.message}
                          labelColor="text-slate-800"
                        />
                      )}
                    />
                    <Controller
                      name="password"
                      control={controlLogin}
                      defaultValue=""
                      render={({ field }) => (
                        <InputField
                          label="Password"
                          type="password"
                          placeholder="Password"
                          {...field}
                          error={errorsLogin.password?.message}
                          labelColor="text-slate-800"
                        />
                      )}
                    />

                    {errorMessage && (
                      <p className="text-sm text-red-600 text-center -mt-2">
                        {errorMessage}
                      </p>
                    )}

                    <button
                      type="submit"
                      className="mt-4 w-full bg-cyan-600 text-white py-3 rounded-xl hover:bg-slate-700 disabled:opacity-50"
                      disabled={isSubmittingLogin}
                    >
                      {isSubmittingLogin ? "Processing..." : "Login"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleModeChange("forgot-password")}
                      className="mt-4 w-full text-cyan-600 text-sm hover:underline flex justify-center"
                    >
                      Forgot password?
                    </button>

                    <div className="text-center text-sm text-slate-800">
                      <p>
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => handleModeChange("register")}
                          className="text-cyan-600 hover:underline"
                        >
                          Register here
                        </button>
                      </p>
                    </div>
                  </motion.form>
                )}
              </motion.div>
            )}

            {formMode === "forgot-password" && (
              <motion.div
                key="forgot-password"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                <h2 className="text-2xl font-semibold text-center mb-6 text-slate-800">
                  Forgot Password
                </h2>
                <form
                  onSubmit={handleSubmitReset(handleResetPassword)}
                  className="flex flex-col gap-3"
                >
                  <Controller
                    name="email"
                    control={controlReset}
                    defaultValue=""
                    render={({ field }) => (
                      <InputField
                        label="Email"
                        type="email"
                        placeholder="Enter your email"
                        {...field}
                        error={errorsReset.email?.message}
                        labelColor="text-slate-800"
                      />
                    )}
                  />

                  {errorMessage && (
                    <p className="text-sm text-red-600 text-center -mt-2">
                      {errorMessage}
                    </p>
                  )}
                  {successMessage && (
                    <p className="text-sm text-slate-800 text-center -mt-2">
                      {successMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="mt-4 w-full bg-cyan-600 text-white py-3 rounded-xl hover:bg-cyan-600 disabled:opacity-50"
                    disabled={isSubmittingReset}
                  >
                    {isSubmittingReset ? "Sending..." : "Send Reset Link"}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => handleModeChange("login")}
                  className="mt-4 w-full text-sm text-cyan-600 hover:underline flex justify-center"
                >
                  Back to Login
                </button>
              </motion.div>
            )}

            {formMode === "register" && (
              <motion.div
                key="register"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                <p className="text-2xl font-semibold mb-6 text-center text-slate-800">
                  Register
                </p>
                <form
                  onSubmit={handleSubmitReg(handleRegister)}
                  className="flex flex-col gap-3"
                >
                  <Controller
                    name="name"
                    control={controlReg}
                    defaultValue=""
                    render={({ field }) => (
                      <InputField
                        label="Name"
                        type="text"
                        placeholder="Full Name"
                        {...field}
                        error={errorsReg.name?.message}
                        labelColor="text-slate-800"
                      />
                    )}
                  />

                  <Controller
                    name="email"
                    control={controlReg}
                    defaultValue=""
                    render={({ field }) => (
                      <InputField
                        label="Email"
                        type="email"
                        placeholder="Email"
                        {...field}
                        error={errorsReg.email?.message}
                        labelColor="text-slate-800"
                      />
                    )}
                  />

                  <Controller
                    name="password"
                    control={controlReg}
                    defaultValue=""
                    render={({ field }) => (
                      <InputField
                        label="Password"
                        type="password"
                        placeholder="Password"
                        {...field}
                        error={errorsReg.password?.message}
                        labelColor="text-slate-800"
                      />
                    )}
                  />

                  {errorMessage && (
                    <p className="text-sm text-red-600 text-center -mt-2">
                      {errorMessage}
                    </p>
                  )}
                  {successMessage && (
                    <p className="text-sm text-slate-800 text-center -mt-2">
                      {successMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="mt-4 w-full bg-cyan-600 text-white py-3 rounded-xl hover:bg-slate-700 disabled:opacity-50"
                    disabled={isSubmittingReg}
                  >
                    {isSubmittingReg ? "Processing..." : "Register"}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => handleModeChange("login")}
                  className="mt-4 w-full text-sm text-cyan-600 hover:underline flex justify-center"
                >
                  Back to Login
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Verification Modal: uses same style/pattern as Profile modals */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
          <div className="bg-white rounded-xl md:border md:border-slate-200 p-4 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-700 mb-4">
              Email Verification
            </h3>

            <div className="space-y-3 text-sm text-slate-700">
              <p>Account {verificationEmail || "you"} is not verified.</p>
              <p>
                Please check your inbox or spam folder for the verification
                email.
              </p>
            </div>

            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="px-8 py-2 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors duration-200 w-full max-w-xs"
              >
                {isResending ? "Sending..." : "Resend"}
              </button>
              <button
                onClick={() => {
                  setShowVerificationModal(false);
                }}
                className="px-8 py-2 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors duration-200 w-full max-w-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
